import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

const DonateBilling = ({ nextStep, prevStep, amount, donationMsg }) => {

  const { t } = useTranslation();
  const API_URL = process.env.REACT_APP_API_URL;

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [phone, setPhone] = useState("");
  const [pan, setPan] = useState("");

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  /* Marathi/Hindi digit conversion */

  const normalizeDigits = (str) => {

    if (!str) return str;

    const map = {
      "०":"0","१":"1","२":"2","३":"3","४":"4",
      "५":"5","६":"6","७":"7","८":"8","९":"9"
    };

    return str.replace(/[०१२३४५६७८९]/g, d => map[d]);

  };

  /* VALIDATION */

  const validate = () => {

    let tempErrors = {};

    const nameRegex = /^[\p{L}\p{M}\s.'-]+$/u;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9]{10}$/;
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

    const normalizedPhone = normalizeDigits(phone);

    if (!firstName.trim())
      tempErrors.firstName = t("errors.first_name_req");

    else if (!nameRegex.test(firstName))
      tempErrors.firstName = t("errors.chars_only");

    if (!lastName.trim())
      tempErrors.lastName = t("errors.last_name_req");

    else if (!nameRegex.test(lastName))
      tempErrors.lastName = t("errors.chars_only");

    if (!email.trim())
      tempErrors.email = t("errors.email_req");

    else if (!emailRegex.test(email))
      tempErrors.email = t("errors.email_invalid");

    if (!normalizedPhone)
      tempErrors.phone = t("errors.phone_req");

    else if (!phoneRegex.test(normalizedPhone))
      tempErrors.phone = t("errors.phone_digits");

    if (pan && !panRegex.test(pan))
      tempErrors.pan = t("errors.pan_invalid");

    setErrors(tempErrors);

    return Object.keys(tempErrors).length === 0;

  };

  /* Load Razorpay */

  const loadScript = (src) => {

    return new Promise((resolve) => {

      const script = document.createElement("script");

      script.src = src;

      script.onload = () => resolve(true);

      script.onerror = () => resolve(false);

      document.body.appendChild(script);

    });

  };

  /* PAYMENT */

  const handleDonate = async () => {

    if (!validate()) return;

    setLoading(true);

    const res = await loadScript("https://checkout.razorpay.com/v1/checkout.js");

    if (!res) {

      Swal.fire({
        icon: "error",
        title: t("swal.payment_error_title"),
        text: t("swal.sdk_failed"),
        confirmButtonColor: "#d33"
      });

      setLoading(false);
      return;

    }

    try {

      const normalizedPhone = normalizeDigits(phone);
      const fullPhone = countryCode.replace("+", "") + normalizedPhone;

      const response = await fetch(`${API_URL}/api/donation/create-order`, {

        method: "POST",

        headers: { "Content-Type": "application/json" },

        body: JSON.stringify({

          amount,
          message: donationMsg,
          firstName,
          lastName,
          email,
          phone: fullPhone,
          pan

        })

      });

      const data = await response.json();

      const options = {

        key: process.env.REACT_APP_RAZORPAY_KEY_ID,

        amount: data.amount,

        currency: "INR",

        name: "Maanavta Hitaay Social Foundation",

        description: t("D.payment_desc"),

        order_id: data.id,

        handler: async function (response) {

          const verifyRes = await fetch(`${API_URL}/api/donation/verify-payment`, {

            method: "POST",

            headers: { "Content-Type": "application/json" },

            body: JSON.stringify({

              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature

            })

          });

          if (verifyRes.ok) {

            Swal.fire({
              icon: "success",
              title: t("swal.success_title"),
              text: t("swal.success_text"),
              confirmButtonColor: "#4b3bbd"
            });

            nextStep();

          } else {

            Swal.fire({
              icon: "error",
              title: t("swal.payment_error_title"),
              text: t("swal.verify_failed"),
              confirmButtonColor: "#d33"
            });

          }

        },

        prefill: {

          name: `${firstName} ${lastName}`,
          email: email,
          contact: fullPhone

        },

        theme: { color: "#4b3bbd" }

      };

      const paymentObject = new window.Razorpay(options);

      paymentObject.open();

    } catch (err) {

      console.error("Payment Error:", err);

    }

    setLoading(false);

  };

  return (

    <div className="d_donate_card">

      <h1>{t("D.billing_title")}</h1>

      {/* First Name */}

      <input
        type="text"
        lang="mr"
        placeholder={t("D.placeholder_first_name")}
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
      />

      {errors.firstName && <p className="error">{errors.firstName}</p>}

      {/* Surname */}

      <input
        type="text"
        lang="mr"
        placeholder={t("D.placeholder_last_name")}
        value={lastName}
        onChange={(e) => setLastName(e.target.value)}
      />

      {errors.lastName && <p className="error">{errors.lastName}</p>}

      {/* Email */}

      <input
        type="email"
        placeholder={t("D.placeholder_email")}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />

      {errors.email && <p className="error">{errors.email}</p>}

      {/* Phone */}

      <div style={{ display: "flex", gap: "10px" }}>

        <input
          style={{ width: "80px" }}
          value={countryCode}
          onChange={(e) => setCountryCode(e.target.value)}
        />

        <input
          type="text"
          inputMode="numeric"
          placeholder={t("D.placeholder_phone")}
          value={phone}
          onChange={(e) => {

            const normalized = normalizeDigits(e.target.value);

            if (!/^[0-9]*$/.test(normalized)) return;

            setPhone(normalized);

          }}
        />

      </div>

      {errors.phone && <p className="error">{errors.phone}</p>}

      {/* PAN */}

      <input
        type="text"
        placeholder={t("D.placeholder_pan")}
        value={pan}
        onChange={(e) => setPan(e.target.value.toUpperCase())}
      />

      {errors.pan && <p className="error">{errors.pan}</p>}

      <div style={{ marginTop: "20px", display: "flex", gap: "10px" }}>

        <button className="d_donate_btn" onClick={prevStep}>
          {t("D.btn_back")}
        </button>

        <button
          className="d_donate_btn"
          onClick={handleDonate}
          disabled={loading}
        >

          {loading
            ? t("D.btn_processing")
            : `${t("D.btn_donate_now")} ₹${amount}`}

        </button>

      </div>

    </div>

  );

};

export default DonateBilling;